import React, { useState } from 'react';

import { getStoredTokens } from '../../../adapters/api-client/tokenStorage';
import { REPORT_REASON_LABEL, ReportReason, reportListing } from '../api/moderation.api';

/**
 * ReportButton (T0 Epic D.1) — buyer-facing "report this listing" control on
 * the PDP. Inline reason picker; requires auth (delegates the login bounce to
 * the parent via onRequireLogin).
 */
export interface ReportButtonProps {
  vehicleId: string;
  onRequireLogin?: () => void;
}

type State = 'idle' | 'open' | 'submitting' | 'done' | 'failed';

export const ReportButton: React.FC<ReportButtonProps> = ({ vehicleId, onRequireLogin }) => {
  const [state, setState] = useState<State>('idle');
  const [reason, setReason] = useState<ReportReason>('FRAUD');
  const [note, setNote] = useState('');

  const open = () => {
    if (!getStoredTokens().idToken) {
      onRequireLogin?.();
      return;
    }
    setState('open');
  };

  const submit = async () => {
    setState('submitting');
    try {
      await reportListing(vehicleId, reason, note.trim() || undefined);
      setState('done');
    } catch {
      setState('failed');
    }
  };

  if (state === 'done') {
    return (
      <p className="caria-report__done" data-testid="report-done">
        Gracias. Revisaremos este anuncio.
      </p>
    );
  }

  if (state !== 'open' && state !== 'submitting' && state !== 'failed') {
    return (
      <button
        type="button"
        className="caria-report__trigger"
        onClick={open}
        data-testid="report-trigger"
      >
        Reportar anuncio
      </button>
    );
  }

  return (
    <div className="caria-report" data-testid="report-form">
      <label htmlFor="report-reason">Motivo</label>
      <select
        id="report-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value as ReportReason)}
      >
        {(Object.keys(REPORT_REASON_LABEL) as ReportReason[]).map((r) => (
          <option key={r} value={r}>
            {REPORT_REASON_LABEL[r]}
          </option>
        ))}
      </select>
      <textarea
        placeholder="Detalle (opcional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={1000}
      />
      {state === 'failed' && (
        <p className="caria-report__error" role="alert">
          No se pudo enviar. Intenta de nuevo.
        </p>
      )}
      <div className="caria-report__actions">
        <button type="button" onClick={submit} disabled={state === 'submitting'}>
          {state === 'submitting' ? 'Enviando…' : 'Enviar reporte'}
        </button>
        <button type="button" onClick={() => setState('idle')} disabled={state === 'submitting'}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default ReportButton;
